<template>
  <div class="conversation-list">
    <!-- Header -->
    <div class="list-header">
      <h3>Messages</h3>
      <button
        class="new-conversation-btn"
        @click="$emit('new-conversation')"
        title="New conversation"
      >
        <i class="fa-solid fa-plus"></i>
      </button>
    </div>

    <!-- Search -->
    <div class="search-container">
      <i class="fa-solid fa-search search-icon"></i>
      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        placeholder="Search conversations..."
      />
    </div>

    <!-- Conversation Items -->
    <div class="conversations-container">
      <div v-if="loading" class="loading-state">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Loading conversations...</span>
      </div>

      <div v-else-if="filteredConversations.length === 0" class="empty-state">
        <i class="fa-regular fa-comments"></i>
        <p v-if="searchQuery">No conversations match your search</p>
        <p v-else>No conversations yet</p>
      </div>

      <div
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        :class="[
          'conversation-item',
          { active: activeConversationId === conversation.id }
        ]"
        @click="$emit('select', conversation)"
      >
        <div class="conversation-avatar">
          <i
            :class="conversation.type === 'group' ? 'fa-solid fa-users' : 'fa-solid fa-user'"
          ></i>
        </div>

        <div class="conversation-info">
          <div class="conversation-header">
            <span class="conversation-name">
              {{ getConversationName(conversation) }}
            </span>
            <span v-if="conversation.lastMessage" class="conversation-time">
              {{ formatTime(conversation.lastMessage.createdAt || conversation.last_message_at) }}
            </span>
          </div>

          <div class="conversation-preview">
            <span class="preview-text">
              {{ getPreviewText(conversation) }}
            </span>
            <i
              v-if="conversation.lastMessage && conversation.lastMessage.attachments && conversation.lastMessage.attachments.length > 0"
              class="fa-solid fa-paperclip attachment-indicator"
              title="Has attachment"
            ></i>
            <span
              v-if="conversation.unreadCount > 0"
              class="unread-badge"
            >
              {{ conversation.unreadCount > 99 ? '99+' : conversation.unreadCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMessagesStore } from "@/stores/messagesStore";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "ConversationList",
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    activeConversationId: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      messagesStore: useMessagesStore(),
      loginStore: useLoginStore(),
    };
  },
  computed: {
    filteredConversations() {
      if (!this.searchQuery.trim()) {
        return this.conversations;
      }

      const query = this.searchQuery.toLowerCase();
      return this.conversations.filter((conv) => {
        const name = this.getConversationName(conv).toLowerCase();
        const preview = this.getPreviewText(conv).toLowerCase();
        return name.includes(query) || preview.includes(query);
      });
    },
  },
  methods: {
    getConversationName(conversation) {
      return this.messagesStore.getConversationDisplayName(conversation);
    },

    getPreviewText(conversation) {
      if (!conversation.lastMessage) {
        return "No messages yet";
      }

      const message = conversation.lastMessage;
      const senderName = message.sender
        ? message.sender.id === this.loginStore.user?.id
          ? "You"
          : message.sender.first_name
        : "";

      const content = message.content || "";
      const truncatedContent = content.length > 40
        ? content.substring(0, 40) + "..."
        : content;

      return senderName ? `${senderName}: ${truncatedContent}` : truncatedContent;
    },

    formatTime(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.conversation-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  overflow: hidden;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.list-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.new-conversation-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #6BA6CD, #4a8ab8);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.new-conversation-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 10px rgba(107, 166, 205, 0.4);
}

.search-container {
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.search-icon {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 36px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #6BA6CD;
  box-shadow: 0 0 0 2px rgba(107, 166, 205, 0.2);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.conversations-container {
  flex: 1;
  overflow-y: auto;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.4);
}

.loading-state i,
.empty-state i {
  font-size: 36px;
  margin-bottom: 12px;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

.conversation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.conversation-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.conversation-item.active {
  background: rgba(107, 166, 205, 0.15);
  border-left: 3px solid #6BA6CD;
}

.conversation-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

.conversation-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.conversation-name {
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  margin-left: 8px;
}

.conversation-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attachment-indicator {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-left: 4px;
  flex-shrink: 0;
}

.unread-badge {
  background: linear-gradient(135deg, #6BA6CD, #4a8ab8);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  margin-left: 8px;
}

/* Scrollbar styling */
.conversations-container::-webkit-scrollbar {
  width: 6px;
}

.conversations-container::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.conversations-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.25);
}
</style>
